import { atom } from 'jotai';

import { ADVANCED_FILTER_KEYS, type IndividualPaymentFilters } from '../../types/individualPayment';
import { parseLookupId } from '../../utils/buildQueryParams';

/** Estado inicial dos filtros — todos vazios. */
export const defaultIndividualPaymentFilters: IndividualPaymentFilters = {
  personId: '',
  studentLabel: '',
  studentName: '',
  studentEmail: '',
  companyId: '',
  companyLabel: '',
  classStudentId: '',
  classStudentLabel: '',
  paymentMethod: '',
  ra: '',
  cpf: '',
  email: '',
  thirdPartyCpf: '',
  thirdPartyCnpj: '',
  ourNumber: '',
  invoiceNumber: '',
};

/**
 * Filtros em edição — alterados em tempo real pelos inputs da
 * `IndividualPaymentFilterBar`. Não disparam busca por si só; servem apenas
 * como rascunho até o operador confirmar com "Pesquisar".
 */
export const individualPaymentFiltersAtom = atom<IndividualPaymentFilters>(
  defaultIndividualPaymentFilters,
);

/**
 * Filtros efetivamente aplicados à query da listagem. `null` enquanto nenhuma
 * pesquisa foi executada — é esse `null` que mantém a tela no estado inicial e
 * a query desabilitada.
 */
export const individualPaymentAppliedFiltersAtom = atom<IndividualPaymentFilters | null>(null);

/** Campos que só contam como filtro quando o lookup tem um `id` numérico selecionado. */
const LOOKUP_ID_KEYS = ['personId', 'companyId', 'classStudentId'] as const;

/**
 * Indica se há ao menos um filtro **utilizável** preenchido — usado para
 * habilitar o botão "Pesquisar".
 *
 * Os lookups contam apenas quando o `id` selecionado converte para número, o
 * mesmo critério de `parseLookupId` na montagem da query. Aceitar um `id` não
 * numérico habilitaria a busca com um filtro que seria descartado adiante — era
 * exatamente o sintoma do aluno, cuja chave (`"USR260953"`) entrava crua no
 * filtro: a tela mostrava o aluno selecionado e a requisição saía sem filtro
 * nenhum. O nome/e-mail derivados do rótulo também habilitam a busca, para que
 * digitar o nome sem selecionar funcione.
 */
export function hasAnyIndividualPaymentFilter(filters: IndividualPaymentFilters): boolean {
  const hasLookup = LOOKUP_ID_KEYS.some(key => parseLookupId(filters[key]) !== undefined);
  const hasAdvanced = ADVANCED_FILTER_KEYS.some(key => filters[key].trim() !== '');
  const hasStudent = filters.studentName.trim() !== '' || filters.studentEmail.trim() !== '';
  return hasLookup || hasAdvanced || hasStudent || filters.paymentMethod.trim() !== '';
}

/**
 * Quantidade de filtros preenchidos no painel "Filtros avançados" — exibida
 * como contador no botão que abre o painel e no cabeçalho dele.
 */
export function countActiveAdvancedFilters(filters: IndividualPaymentFilters): number {
  return ADVANCED_FILTER_KEYS.filter(key => filters[key].trim() !== '').length;
}
